import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  hardwareAPI,
  companiesAPI,
  type Logger,
  type LicenceType,
  type BulkUpdateFields,
  type BulkUpdateResponse,
} from '../../services';
import { HARDWARE_STATUS_OPTIONS, LOGGER_FREQUENCY_OPTIONS } from './hardwareDisplay';
import {
  CALIBRATION_FREQUENCY_OPTIONS,
  HDTECH_COMPANY_ID,
  SIM_STATUS_OPTIONS,
  SIM_TYPE_OPTIONS,
  SUBSCRIPTION_OPTIONS,
  dateInputToIso,
  type CompanyOption,
} from './hardwareFormModel';

type FieldKey =
  | 'Status' | 'SaleOrHire' | 'CompanyID' | 'HireStartDate' | 'ReturnDate'
  | 'LicenceTypeID' | 'LicenceStartDate' | 'SubscriptionMonths'
  | 'SIMStatus' | 'SIMType' | 'CalibrationFrequencyNumber' | 'LoggerFrequency' | 'Note';

type Values = Record<FieldKey, string>;

const EMPTY: Values = {
  Status: '', SaleOrHire: '', CompanyID: '', HireStartDate: '', ReturnDate: '',
  LicenceTypeID: '', LicenceStartDate: '', SubscriptionMonths: '',
  SIMStatus: '', SIMType: '', CalibrationFrequencyNumber: '', LoggerFrequency: '', Note: '',
};

// Selects where "None" is a real choice that clears the value on each device.
const CLEARABLE: FieldKey[] = ['CompanyID', 'LicenceTypeID', 'SubscriptionMonths'];

const FIELD_LABELS: Record<FieldKey, string> = {
  Status: 'Status',
  SaleOrHire: 'Purpose',
  CompanyID: 'Allocated / hired to',
  HireStartDate: 'Hire Start',
  ReturnDate: 'Expected Return',
  LicenceTypeID: 'Software Licence Type',
  LicenceStartDate: 'Software Licence Start Date',
  SubscriptionMonths: 'Software Licence Period',
  SIMStatus: 'SIM Card Status',
  SIMType: 'SIM Card Type',
  CalibrationFrequencyNumber: 'Calibration Frequency',
  LoggerFrequency: 'Measurement Frequency',
  Note: 'Note',
};

const SCHEDULED_TYPES = ['01', '02', '03', '04', '10'];

interface Props {
  open: boolean;
  devices: Logger[];
  onClose: () => void;
  /** Called after a run with the ids that saved, so the list can refresh. */
  onApplied: (updatedIds: string[]) => void;
}

type Step = 'edit' | 'review' | 'results';

/**
 * Bulk edit for super admins: tick the fields to change, review, apply.
 * Each device is saved on its own by the backend with the single-device
 * rules, so results are shown per device. Mode is never changed here.
 */
const BulkEditDialog: React.FC<Props> = ({ open, devices, onClose, onApplied }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('edit');
  const [enabled, setEnabled] = useState<Set<FieldKey>>(new Set());
  const [values, setValues] = useState<Values>(EMPTY);
  const [noteMode, setNoteMode] = useState<'append' | 'replace'>('append');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [licenceTypes, setLicenceTypes] = useState<LicenceType[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState<BulkUpdateResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('edit');
    setEnabled(new Set());
    setValues(EMPTY);
    setNoteMode('append');
    setError('');
    setResponse(null);
    companiesAPI
      .getCompanies()
      .then((list) =>
        setCompanies(
          (Array.isArray(list) ? list : [])
            .filter((c: any) => String(c.companyid) !== HDTECH_COMPANY_ID && c.isactive !== false)
            .map((c: any) => ({ id: String(c.companyid), name: c.companyname }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => {});
    hardwareAPI.getLicenceTypes().then((l) => setLicenceTypes(Array.isArray(l) ? l : [])).catch(() => {});
  }, [open]);

  const toggle = (key: FieldKey) => (on: boolean) =>
    setEnabled((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  const set = (key: FieldKey) => (v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    toggle(key)(true);
  };

  // A ticked field needs a value, unless "None" is a real choice for it.
  const missing = Array.from(enabled).filter((k) => !values[k].trim() && !CLEARABLE.includes(k));
  const canReview = enabled.size > 0 && missing.length === 0;

  const companyName = (id: string) =>
    id ? companies.find((c) => c.id === id)?.name || id : t('hardware.notSoldOrHired', 'HDTECH (not sold or hired)');

  const displayOf = (key: FieldKey): string => {
    const v = values[key];
    switch (key) {
      case 'Status': return HARDWARE_STATUS_OPTIONS.find((o) => o.value === v)?.label || v;
      case 'CompanyID': return companyName(v);
      case 'LicenceTypeID': return v ? licenceTypes.find((l) => l.LicenceTypeID === v)?.Name || v : t('hardware.clear', 'None (clear)');
      case 'SubscriptionMonths': return v ? `${v} months` : t('hardware.clear', 'None (clear)');
      case 'CalibrationFrequencyNumber': return `${v} months`;
      case 'LoggerFrequency': return LOGGER_FREQUENCY_OPTIONS.find((o) => o.value === v)?.label || v;
      case 'Note': return `${noteMode === 'append' ? 'Add' : 'Replace with'}: "${v.trim()}"`;
      default: return v;
    }
  };

  const buildFields = (): BulkUpdateFields => {
    const f: BulkUpdateFields = {};
    enabled.forEach((key) => {
      const v = values[key];
      switch (key) {
        case 'HireStartDate':
        case 'ReturnDate':
        case 'LicenceStartDate':
          f[key] = dateInputToIso(v);
          break;
        case 'SubscriptionMonths':
          f.SubscriptionMonths = v ? Number(v) : 0;
          break;
        case 'CalibrationFrequencyNumber':
          f.CalibrationFrequencyNumber = Number(v);
          break;
        default:
          (f as Record<string, string>)[key] = v;
      }
    });
    return f;
  };

  // Heads-ups for the review step, worked out from the rows we already have.
  const warnings = useMemo(() => {
    const out: string[] = [];
    const count = (pred: (l: Logger) => boolean) => devices.filter(pred).length;
    if (enabled.has('CompanyID')) {
      const live = count((l) => !!l.OnLivePour);
      if (live) out.push(`${live} device(s) are on a live pour. Sold devices on a live pour keep their client; hire devices ask you to confirm after the first pass.`);
    }
    if (enabled.has('SIMStatus') || enabled.has('SIMType')) {
      const n = count((l) => l.Type !== '01');
      if (n) out.push(`SIM fields only apply to ConXagents; ${n} device(s) will skip them.`);
    }
    if (enabled.has('CalibrationFrequencyNumber') || enabled.has('LoggerFrequency')) {
      const n = count((l) => !SCHEDULED_TYPES.includes(l.Type || ''));
      if (n) out.push(`Calibration and measurement frequency only apply to loggers; ${n} device(s) will skip them.`);
    }
    if (enabled.has('LoggerFrequency')) {
      out.push('The new frequency is saved in ConXedge. It is not sent to the loggers yet.');
    }
    if (enabled.has('HireStartDate') || enabled.has('ReturnDate')) {
      out.push('Hire dates are only set on devices that end up For Hire with a client.');
    }
    return out;
  }, [devices, enabled]);

  const apply = async (ids: string[], confirmLivePour: boolean) => {
    setSaving(true);
    setError('');
    try {
      const r = await hardwareAPI.bulkUpdate({
        logger_ids: ids,
        fields: buildFields(),
        note_mode: noteMode,
        confirm_live_pour: confirmLivePour || undefined,
      });
      // A confirm re-run only covers the live-pour devices: merge its
      // results into the first pass.
      setResponse((prev) => {
        if (!prev || !confirmLivePour) return r;
        const byId = new Map(prev.results.map((x) => [x.logger_id, x]));
        r.results.forEach((x) => byId.set(x.logger_id, x));
        const results = Array.from(byId.values());
        const updated = results.filter((x) => x.ok).length;
        return { results, updated, failed: results.length - updated };
      });
      setStep('results');
      onApplied(r.results.filter((x) => x.ok).map((x) => x.logger_id));
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('hardware.bulkFailed', 'Bulk update failed'));
    } finally {
      setSaving(false);
    }
  };

  const label = (l: Logger) => l.HMTechNumber || l.SerialNumber || l.LoggerID;
  const nameOf = (id: string, fallback?: string | null) => {
    const d = devices.find((x) => x.LoggerID === id);
    return d ? label(d) : fallback || id;
  };

  // ---- Field rows ---------------------------------------------------------

  const row = (key: FieldKey, input: React.ReactNode) => (
    <Box
      key={key}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '220px minmax(0, 1fr)' },
        alignItems: 'center',
        columnGap: 2,
        py: 0.5,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={enabled.has(key)}
            onChange={(e) => toggle(key)(e.target.checked)}
            inputProps={{ 'aria-label': `Change ${FIELD_LABELS[key]}` }}
          />
        }
        label={<Typography variant="body2">{t(`hardware.bulk.${key}`, FIELD_LABELS[key])}</Typography>}
        sx={{ mr: 0 }}
      />
      <Box sx={{ minWidth: 0, opacity: enabled.has(key) ? 1 : 0.6 }}>{input}</Box>
    </Box>
  );

  const select = (
    key: FieldKey,
    options: { value: string; label: string }[],
    emptyLabel?: string,
  ) =>
    row(
      key,
      <TextField
        select fullWidth size="small" value={values[key]}
        onChange={(e) => set(key)(e.target.value)}
        slotProps={{ select: { displayEmpty: true } }}
        inputProps={{ 'aria-label': FIELD_LABELS[key] }}
      >
        <MenuItem value="">
          <em>{emptyLabel || t('hardware.pick', 'Pick a value')}</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </TextField>,
    );

  const date = (key: FieldKey) =>
    row(
      key,
      <TextField
        fullWidth size="small" type="date" value={values[key]}
        onChange={(e) => set(key)(e.target.value)}
        inputProps={{ 'aria-label': FIELD_LABELS[key] }}
      />,
    );

  const clearLabel = t('hardware.clear', 'None (clear)');
  const selectedCompany = companies.find((c) => c.id === values.CompanyID) ?? null;

  const editStep = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t(
          'hardware.bulkIntro',
          'Tick each field to change. Fields you leave unticked stay as they are on every device.',
        )}
      </Typography>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Assignment</Typography>
      {select('Status', HARDWARE_STATUS_OPTIONS)}
      {select('SaleOrHire', [
        { value: 'For Sale', label: t('hardware.forSale', 'For Sale') },
        { value: 'For Hire', label: t('hardware.forHire', 'For Hire') },
      ])}
      {row(
        'CompanyID',
        <Autocomplete
          options={companies}
          value={selectedCompany}
          onChange={(_, v) => set('CompanyID')(v ? v.id : '')}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={t('hardware.notSoldOrHired', 'HDTECH (not sold or hired)')}
              inputProps={{ ...params.inputProps, 'aria-label': FIELD_LABELS.CompanyID }}
            />
          )}
        />,
      )}
      {date('HireStartDate')}
      {date('ReturnDate')}

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>Licence</Typography>
      {select(
        'LicenceTypeID',
        licenceTypes.filter((lt) => lt.IsActive !== false).map((lt) => ({ value: lt.LicenceTypeID, label: lt.Name })),
        clearLabel,
      )}
      {date('LicenceStartDate')}
      {select('SubscriptionMonths', SUBSCRIPTION_OPTIONS, clearLabel)}

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>SIM (ConXagent)</Typography>
      {select('SIMStatus', SIM_STATUS_OPTIONS.map((v) => ({ value: v, label: v })))}
      {select('SIMType', SIM_TYPE_OPTIONS.map((v) => ({ value: v, label: v })))}

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>Loggers</Typography>
      {select('CalibrationFrequencyNumber', CALIBRATION_FREQUENCY_OPTIONS)}
      {select('LoggerFrequency', LOGGER_FREQUENCY_OPTIONS)}

      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>Note</Typography>
      {row(
        'Note',
        <Box>
          <TextField
            fullWidth size="small" multiline minRows={2} value={values.Note}
            onChange={(e) => set('Note')(e.target.value)}
            inputProps={{ 'aria-label': FIELD_LABELS.Note }}
          />
          <RadioGroup row value={noteMode} onChange={(e) => setNoteMode(e.target.value as 'append' | 'replace')}>
            <FormControlLabel value="append" control={<Radio size="small" />} label={<Typography variant="body2">Add to the existing note</Typography>} />
            <FormControlLabel value="replace" control={<Radio size="small" />} label={<Typography variant="body2">Replace the note</Typography>} />
          </RadioGroup>
        </Box>,
      )}
      {missing.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('hardware.bulkMissing', 'Pick a value for: {{fields}}', {
            fields: missing.map((k) => FIELD_LABELS[k]).join(', '),
          })}
        </Alert>
      )}
    </>
  );

  const reviewStep = (
    <>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {t('hardware.bulkWillSet', 'These changes go to {{n}} device(s):', { n: devices.length })}
      </Typography>
      <Box component="ul" sx={{ mt: 0, pl: 3 }}>
        {Array.from(enabled).map((k) => (
          <li key={k}>
            <Typography variant="body2">
              <strong>{FIELD_LABELS[k]}</strong>: {displayOf(k)}
            </Typography>
          </li>
        ))}
      </Box>
      {warnings.map((w) => (
        <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
      ))}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, maxHeight: 180, overflowY: 'auto' }}>
        {devices.map((d) => (
          <Chip key={d.LoggerID} size="small" label={label(d)} color={d.OnLivePour ? 'warning' : 'default'} variant="outlined" />
        ))}
      </Box>
    </>
  );

  const livePour = response?.results.filter((r) => !r.ok && r.code === 'LIVE_POUR') ?? [];
  const otherFailures = response?.results.filter((r) => !r.ok && r.code !== 'LIVE_POUR') ?? [];
  const withSkips = response?.results.filter((r) => r.ok && r.skipped.length > 0) ?? [];

  const resultsStep = response && (
    <>
      <Alert severity={response.failed ? 'warning' : 'success'} sx={{ mb: 2 }}>
        {t('hardware.bulkResult', '{{updated}} updated, {{failed}} not updated.', {
          updated: response.updated,
          failed: response.failed,
        })}
      </Alert>
      {livePour.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit" size="small" disabled={saving}
              onClick={() => apply(livePour.map((r) => r.logger_id), true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {t('hardware.bulkConfirmLive', 'Change these too')}
            </Button>
          }
        >
          {t(
            'hardware.bulkLivePour',
            'These hire devices are on a live pour. Changing their company takes them away from the current client: the pour keeps its data, but the client can no longer pick the device.',
          )}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {livePour.map((r) => (
              <li key={r.logger_id}>{nameOf(r.logger_id, r.hm_tech_number)} (pour {r.pour_code})</li>
            ))}
          </Box>
        </Alert>
      )}
      {otherFailures.length > 0 && (
        <>
          <Typography variant="subtitle2">{t('hardware.bulkNotUpdated', 'Not updated')}</Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 3 }}>
            {otherFailures.map((r) => (
              <li key={r.logger_id}>
                <Typography variant="body2">
                  <strong>{nameOf(r.logger_id, r.hm_tech_number)}</strong>: {r.error}
                </Typography>
              </li>
            ))}
          </Box>
        </>
      )}
      {withSkips.length > 0 && (
        <>
          <Typography variant="subtitle2">{t('hardware.bulkSkipped', 'Updated, with fields that do not apply skipped')}</Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 3 }}>
            {withSkips.map((r) => (
              <li key={r.logger_id}>
                <Typography variant="body2">
                  <strong>{nameOf(r.logger_id, r.hm_tech_number)}</strong>:{' '}
                  {r.skipped.map((f) => FIELD_LABELS[f as FieldKey] || f).join(', ')}
                </Typography>
              </li>
            ))}
          </Box>
        </>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        {step === 'results'
          ? t('hardware.bulkResultsTitle', 'Bulk edit results')
          : t('hardware.bulkTitle', 'Edit {{n}} device(s)', { n: devices.length })}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {step === 'edit' && editStep}
        {step === 'review' && reviewStep}
        {step === 'results' && resultsStep}
      </DialogContent>
      <DialogActions>
        {step === 'edit' && (
          <>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('common.cancel', 'Cancel')}</Button>
            <Button variant="contained" disabled={!canReview} onClick={() => setStep('review')} sx={{ textTransform: 'none' }}>
              {t('hardware.bulkReview', 'Review changes')}
            </Button>
          </>
        )}
        {step === 'review' && (
          <>
            <Button onClick={() => setStep('edit')} disabled={saving} sx={{ textTransform: 'none' }}>
              {t('common.back', 'Back')}
            </Button>
            <Button
              variant="contained" disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() => apply(devices.map((d) => d.LoggerID), false)}
              sx={{ textTransform: 'none' }}
            >
              {t('hardware.bulkApply', 'Apply to {{n}} device(s)', { n: devices.length })}
            </Button>
          </>
        )}
        {step === 'results' && (
          <Button variant="contained" onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
            {t('common.done', 'Done')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditDialog;
